import React, { useState, useEffect, useCallback } from 'react';
import { Table, Button, Modal, Form, Select, Space, message, Popconfirm, Alert, Menu, Dropdown, Switch, Tag } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, CopyOutlined, OrderedListOutlined, MoreOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../../services/api';

const { Option } = Select;

const formatColor = (color) => {
  if (!color) return '#000000';
  return color.startsWith('#') ? color : `#${color}`;
};

const MetricSettings = () => {
  const [metrics, setMetrics] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(false);
  const [copyModalVisible, setCopyModalVisible] = useState(false);
  const [copyForm] = Form.useForm();
  const navigate = useNavigate();
  
  // Sıralama ve görünürlük ayarları için state'ler
  const [sortModalVisible, setSortModalVisible] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState(null);
  const [sortableMetrics, setSortableMetrics] = useState([]);
  const [metricVisibility, setMetricVisibility] = useState({});
  const [savingSortOrder, setSavingSortOrder] = useState(false);
  const [targetBrands, setTargetBrands] = useState([]); // Hedef markaları tutacak state
  
  // Template filtreleme için state'ler
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [filteredBrands, setFilteredBrands] = useState([]);

  const fetchMetrics = useCallback(async () => {
    try {
      const response = await api.get('/metrics?scope=all');
      if (response.data) {
        setMetrics(response.data);
      }
    } catch (error) {
      console.error('Metrikler yüklenirken hata:', error);
      message.error('Metrikler yüklenirken bir hata oluştu');
    }
  }, []);

  const fetchBrands = useCallback(async () => {
    try {
      // Önce minimal modda markaları getir
      const response = await api.get('/brands', {
        params: {
          minimal: 'true'
        }
      });
      
      if (!response.data || !Array.isArray(response.data)) {
        console.error('Beklenmeyen API yanıtı:', response.data);
        message.error('Markalar yüklenirken bir hata oluştu');
        return;
      }

      const sortedBrands = response.data
        .filter(brand => brand && brand.name)
        .sort((a, b) => a.name.localeCompare(b.name));

      setBrands(sortedBrands);
      setFilteredBrands(sortedBrands);
      
      // Template listesini oluştur
      const uniqueTemplates = [...new Set(sortedBrands
        .filter(brand => brand.template)
        .map(brand => brand.template))]
        .sort();
      setTemplates(uniqueTemplates);
      
      // İlk markayı varsayılan olarak seç
      if (sortedBrands.length > 0 && !selectedBrand) {
        setSelectedBrand(sortedBrands[0].id);
      }
    } catch (error) {
      console.error('Marka yükleme hatası:', error.response || error);
      message.error('Markalar yüklenirken bir hata oluştu');
    }
  }, [selectedBrand]);

  // Marka metriklerini ayrı ayrı yükle - ihtiyaç duyulduğunda
  const loadBrandMetrics = useCallback(async (brandId) => {
    try {
      // Marka metriklerini getir
      const response = await api.get(`/brands/${brandId}/metrics`);
      
      if (!response.data || !Array.isArray(response.data)) {
        console.error('Beklenmeyen API yanıtı:', response.data);
        return [];
      }
      
      return response.data;
    } catch (error) {
      console.error(`${brandId} ID'li marka için metrikler yüklenirken hata:`, error);
      return [];
    }
  }, []);
  
  // Template değiştiğinde markaları filtrele
  useEffect(() => {
    if (selectedTemplate) {
      const filtered = brands.filter(brand => brand.template === selectedTemplate);
      setFilteredBrands(filtered);
      
      // Template değiştiğinde hedef markaları da güncelle
      setTargetBrands(filtered.map(brand => brand.id));
    } else {
      setFilteredBrands(brands);
    }
  }, [selectedTemplate, brands]);
  
  // Seçilen marka için metrik sıralama ve görünürlük bilgilerini getir
  const fetchBrandMetricDetails = async (brandId) => {
    try {
      setLoading(true);
      
      // Marka metriklerini getir
      const response = await api.get(`/brands/${brandId}`, {
        params: {
          includeMetrics: true
        }
      });
      
      if (!response.data || !response.data.Metrics) {
        message.error('Marka metrik detayları yüklenemedi');
        setLoading(false);
        return;
      }
      
      // Metrikleri displayOrder'a göre sırala
      const sortedMetrics = [...response.data.Metrics].sort((a, b) => {
        const orderA = a.BrandMetric?.displayOrder !== undefined ? a.BrandMetric.displayOrder : Number.MAX_SAFE_INTEGER;
        const orderB = b.BrandMetric?.displayOrder !== undefined ? b.BrandMetric.displayOrder : Number.MAX_SAFE_INTEGER;
        return orderA - orderB;
      });
      
      // Sıralanabilir metrikleri oluştur
      const sortableItems = sortedMetrics.map(metric => ({
        id: metric.id,
        name: metric.name,
        displayOrder: metric.BrandMetric?.displayOrder !== undefined ? metric.BrandMetric.displayOrder : 0,
        isVisible: metric.BrandMetric?.isVisible !== undefined ? metric.BrandMetric.isVisible : true,
        description: metric.description,
        isAutoCalculated: metric.isAutoCalculated,
        color: metric.color
      }));
      
      setSortableMetrics(sortableItems);
      
      // Görünürlük durumlarını ayarla
      const visibilityMap = {};
      sortedMetrics.forEach(metric => {
        visibilityMap[metric.id] = metric.BrandMetric?.isVisible !== undefined ? metric.BrandMetric.isVisible : true;
      });
      setMetricVisibility(visibilityMap);
    } catch (error) {
      console.error('Marka metrik detayları yüklenirken hata:', error);
      message.error('Marka metrik detayları yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;
    setLoading(true);

    const loadData = async () => {
      try {
        // Önce temel verileri yükle
        await Promise.all([
          fetchMetrics(),
          fetchBrands()
        ]);
        
        // Daha sonra ihtiyaç duyulduğunda marka metriklerini yükle
        // Bu kısım, kullanıcı bir marka seçtiğinde veya bir işlem yaptığında çağrılabilir
      } catch (error) {
        console.error('Veri yükleme hatası:', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [fetchMetrics, fetchBrands]);

  const handleDelete = async (id) => {
    try {
      setLoading(true);
      const response = await api.delete(`/metrics/${id}`);
      if (response.status === 204) {
        message.success('Metrik başarıyla silindi');
        await fetchMetrics();
      }
    } catch (error) {
      console.error('Metrik silme hatası:', error);
      if (error.response?.data?.error) {
        message.error({
          content: error.response.data.error,
          duration: 5,
          style: {
            marginTop: '20vh',
          },
        });
      } else {
        message.error('Metrik silinirken bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopyMetrics = async () => {
    try {
      const values = await copyForm.validateFields();
      setLoading(true);

      // Önce kaynak markanın metriklerini kontrol et
      const sourceBrandMetricsResponse = await api.get(`/brands/${values.sourceBrandId}`, {
        params: {
          includeMetrics: true,
          includeMonthlyMetrics: true
        }
      });
      
      const sourceBrandData = sourceBrandMetricsResponse.data;
      if (!sourceBrandData || !sourceBrandData.Metrics) {
        message.warning('Kaynak markada kopyalanacak metrik bulunamadı');
        setLoading(false);
        return;
      }

      // Kopyalama işlemini gerçekleştir
      const response = await api.post('/brands/copy-metrics', {
        sourceBrandId: values.sourceBrandId,
        targetBrandId: values.targetBrandId,
        skipMetricCodes: true // Metrik kodlarını kopyalama
      });

      if (response.data.success) {
        const sourceBrand = brands.find(b => b.id === values.sourceBrandId);
        const targetBrand = brands.find(b => b.id === values.targetBrandId);
        
        message.success(
          `Metrikler başarıyla kopyalandı:\n` +
          `${sourceBrand.name} -> ${targetBrand.name}\n` +
          `Günlük Metrikler: ${response.data.dailyMetricsCount}\n` +
          `Aylık Metrikler: ${response.data.monthlyMetricsCount}\n` +
          `Not: Metrik kodları marka özel olduğu için kopyalanmadı.`
        );
        
        setCopyModalVisible(false);
        copyForm.resetFields();
        
        // Markaları yeniden yükle
        await fetchBrands();
      }
    } catch (error) {
      console.error('Metrik kopyalama hatası:', error);
      
      // Hata mesajını daha detaylı göster
      if (error.response?.data?.error) {
        message.error({
          content: `Metrikler kopyalanırken bir hata oluştu: ${error.response.data.error}`,
          duration: 5,
          style: {
            marginTop: '20vh',
          },
        });
      } else {
        message.error('Metrikler kopyalanırken bir hata oluştu');
      }
    } finally {
      setLoading(false);
    }
  };
  
  // Sıralama modali işlemleri
  const showSortModal = () => {
    if (!selectedBrand) {
      message.warning('Lütfen önce bir marka seçin');
      return;
    }
    
    // Seçili template varsa, o template'e ait markaları seç, yoksa tüm markaları seç
    if (selectedTemplate) {
      const templateBrands = brands.filter(brand => brand.template === selectedTemplate);
      setTargetBrands(templateBrands.map(brand => brand.id));
    } else {
      setTargetBrands(brands.map(brand => brand.id));
    }
    
    // Seçilen marka için metrik detaylarını getir
    fetchBrandMetricDetails(selectedBrand);
    setSortModalVisible(true);
  };
  
  // Sürükle bırak işlemi sonunda
  const handleDragEnd = (result) => {
    const { destination, source } = result;
    
    // Eğer hedef yoksa (sürükleme iptal edildiyse) veya aynı konuma bırakıldıysa işlem yapma
    if (!destination || 
        (destination.index === source.index && destination.droppableId === source.droppableId)) {
        return;
    }
    
    const newItems = Array.from(sortableMetrics);
    const [movedItem] = newItems.splice(source.index, 1);
    newItems.splice(destination.index, 0, movedItem);
    
    // DisplayOrder değerlerini güncelle
    const updatedItems = newItems.map((item, index) => ({
        ...item,
        displayOrder: index
    }));
    
    setSortableMetrics(updatedItems);
  };
  
  // Görünürlük değiştirme işlemi
  const handleVisibilityChange = (metricId, checked) => {
    setMetricVisibility(prev => ({
        ...prev,
        [metricId]: checked
    }));
  };
  
  // Tüm metriklerin görünürlüğünü ayarla
  const handleSetAllVisibility = (visibleState) => {
    const newVisibility = {};
    sortableMetrics.forEach(metric => {
        newVisibility[metric.id] = visibleState;
    });
    setMetricVisibility(newVisibility);
  };
  
  // Seçili markalara sıralama ve görünürlük ayarlarını kaydet
  const saveSortOrder = async () => {
    if (!selectedBrand) {
      message.warning('Lütfen önce bir referans marka seçin');
      return;
    }
    
    if (targetBrands.length === 0) {
      message.warning('Lütfen en az bir hedef marka seçin');
      return;
    }
    
    try {
      setSavingSortOrder(true);
      
      // Metrik sıralama ve görünürlük bilgilerini hazırla
      const updatedMetrics = sortableMetrics.map(metric => ({
        id: parseInt(metric.id),
        displayOrder: metric.displayOrder,
        isVisible: metricVisibility[metric.id]
      }));
      
      // Önce kullanıcıyı bilgilendir
      message.loading({
        content: 'Seçili markalar için metrik sıralama ve görünürlük ayarları güncelleniyor...',
        key: 'sortOrderLoading',
        duration: 0
      });
      
      // Seçili markalar için sıralama ve görünürlük ayarlarını güncelle
      const response = await api.patch('/metrics/display-order/all-brands', {
        metrics: updatedMetrics,
        brandIds: targetBrands // Seçili markaların ID'lerini gönder
      });
      
      if (response.data && response.data.success) {
        message.success({
          content: `Metrik sıralaması ve görünürlük ayarları ${response.data.brandCount || targetBrands.length} marka için başarıyla güncellendi`,
          key: 'sortOrderLoading',
          duration: 3
        });
        setSortModalVisible(false);
        // Güncellenmiş metrikleri getir
        await fetchMetrics();
      } else {
        throw new Error(response.data?.error || 'Bir hata oluştu');
      }
    } catch (error) {
      console.error('Sıralama ve görünürlük ayarları kaydetme hatası:', error);
      message.error({
        content: 'Ayarlar kaydedilirken bir hata oluştu',
        key: 'sortOrderLoading'
      });
    } finally {
      setSavingSortOrder(false);
    }
  };

  const handleBrandSelect = async (brandId) => {
    try {
      setLoading(true);
      
      // Seçilen markanın metrik detaylarını getir
      const response = await api.get(`/brands/${brandId}`, {
        params: {
          includeMetrics: true,
          includeMonthlyMetrics: true
        }
      });
      
      if (!response.data) {
        message.error('Marka detayları yüklenemedi');
        return;
      }
      
      // Seçilen markanın detaylarını güncelle
      const updatedBrands = brands.map(brand => {
        if (brand.id === brandId) {
          return {
            ...brand,
            ...response.data
          };
        }
        return brand;
      });
      
      setBrands(updatedBrands);
      message.success('Marka detayları yüklendi');
    } catch (error) {
      console.error('Marka detayları yüklenirken hata:', error);
      message.error('Marka detayları yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const hasBrands = () => {
    return brands && brands.length > 0;
  };
  
  // Sıralama modalı render
  const renderSortModal = () => (
    <Modal
      title={`Metrik Sıralama ve Görünürlük Ayarları`}
      open={sortModalVisible}
      onCancel={() => setSortModalVisible(false)}
      width={800}
      footer={[
        <Button key="cancel" onClick={() => setSortModalVisible(false)}>
          İptal
        </Button>,
        <Button 
          key="save" 
          type="primary" 
          loading={savingSortOrder}
          onClick={saveSortOrder}
        >
          Seçili Markalara Uygula
        </Button>
      ]}
    >
      <div style={{ marginBottom: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }}>
          <Space>
            <span>Referans Marka Seçin:</span>
            <Select
              value={selectedBrand}
              onChange={(value) => {
                setSelectedBrand(value);
                fetchBrandMetricDetails(value);
              }}
              style={{ width: 200 }}
              placeholder="Marka seçin"
            >
              {filteredBrands.map(brand => (
                <Select.Option key={brand.id} value={brand.id}>
                  {brand.name}
                  {brand.template && <span style={{ color: '#888', marginLeft: 5 }}>({brand.template})</span>}
                </Select.Option>
              ))}
            </Select>
          </Space>
          
          {templates.length > 0 && (
            <Space>
              <span>Template Filtresi:</span>
              <Select
                placeholder="Template seçin"
                allowClear
                style={{ width: 200 }}
                value={selectedTemplate}
                onChange={(value) => {
                  setSelectedTemplate(value);
                  if (value) {
                    const templateBrands = brands.filter(brand => brand.template === value);
                    setTargetBrands(templateBrands.map(brand => brand.id));
                  }
                }}
              >
                {templates.map(template => (
                  <Select.Option key={template} value={template}>{template}</Select.Option>
                ))}
              </Select>
            </Space>
          )}
          
          <Space direction="vertical" style={{ width: '100%' }}>
            <span>Ayarları Uygulanacak Markalar:</span>
            <Select
              mode="multiple"
              placeholder="Markalar seçin"
              value={targetBrands}
              onChange={setTargetBrands}
              style={{ width: '100%' }}
              optionFilterProp="children"
            >
              {filteredBrands.map(brand => (
                <Select.Option key={brand.id} value={brand.id}>
                  {brand.name}
                  {brand.template && <span style={{ color: '#888', marginLeft: 5 }}>({brand.template})</span>}
                </Select.Option>
              ))}
            </Select>
            <Space>
              <Button 
                size="small" 
                onClick={() => setTargetBrands(filteredBrands.map(brand => brand.id))}
              >
                Tümünü Seç
              </Button>
              <Button 
                size="small" 
                onClick={() => setTargetBrands([])}
              >
                Tümünü Temizle
              </Button>
            </Space>
          </Space>
          
          <Space>
            <Button onClick={() => handleSetAllVisibility(true)}>
              Tümünü Göster
            </Button>
            <Button onClick={() => handleSetAllVisibility(false)}>
              Tümünü Gizle
            </Button>
          </Space>
          
          <Alert
            message="Seçili Markalara Uygulama"
            description={
              <div>
                <p><strong>Not:</strong> Seçtiğiniz referans markanın metrik sıralama ve görünürlük ayarları, sadece yukarıda seçtiğiniz markalar için uygulanacaktır.</p>
                <p>Metrikleri sıralamak için sürükleyip bırakın. Görünürlük ayarlarını değiştirmek için anahtarları kullanın.</p>
                {selectedTemplate && <p><strong>Template Filtresi Aktif:</strong> Şu anda sadece <strong>{selectedTemplate}</strong> template'ine sahip markalar görüntülenmektedir.</p>}
              </div>
            }
            type="info"
            showIcon
          />
        </Space>
      </div>
      
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="metrics-list">
          {(provided) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
            >
              {sortableMetrics.map((metric, index) => (
                <Draggable 
                  key={metric.id.toString()} 
                  draggableId={metric.id.toString()} 
                  index={index}
                >
                  {(provided, snapshot) => (
                    <div
                      ref={provided.innerRef}
                      {...provided.draggableProps}
                      {...provided.dragHandleProps}
                      style={{
                        ...provided.draggableProps.style,
                        userSelect: 'none',
                        padding: 16,
                        margin: '0 0 8px 0',
                        backgroundColor: snapshot.isDragging ? '#f0f0f0' : 'white',
                        border: '1px solid #d9d9d9',
                        borderRadius: 4,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                        <div style={{ marginRight: 16, fontWeight: 'bold', width: 30 }}>
                          {index + 1}
                        </div>
                        <div style={{ 
                          width: 24, 
                          height: 24, 
                          backgroundColor: formatColor(metric.color), 
                          borderRadius: '50%',
                          border: '1px solid #ddd',
                          marginRight: 16
                        }} />
                        <div>
                          <div style={{ fontWeight: 'bold' }}>{metric.name}</div>
                          <div style={{ fontSize: '12px', color: '#888' }}>
                            {metric.isAutoCalculated ? 'Otomatik Hesaplanan' : 'Manuel'}
                          </div>
                        </div>
                      </div>
                      <div>
                        <Space>
                          <span style={{ marginRight: 8 }}>
                            {metricVisibility[metric.id] === true ? 
                              <Tag color="success">Görünür</Tag> : 
                              <Tag color="default">Gizli</Tag>
                            }
                          </span>
                          <Switch
                            checked={metricVisibility[metric.id] === true}
                            onChange={(checked) => handleVisibilityChange(metric.id, checked)}
                            checkedChildren={<EyeOutlined />}
                            unCheckedChildren={<EyeInvisibleOutlined />}
                          />
                        </Space>
                      </div>
                    </div>
                  )}
                </Draggable>
              ))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
    </Modal>
  );

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 80
    },
    {
      title: 'Metrik Adı',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.localeCompare(b.name)
    },
    {
      title: 'Açıklama',
      dataIndex: 'description',
      key: 'description',
      render: (text) => text || '-'
    },
    {
      title: 'Veri Tipi',
      dataIndex: 'dataType',
      key: 'dataType',
      render: (text) => {
        switch (text) {
          case 'number': return 'Sayı';
          case 'currency': return 'Para Birimi';
          case 'percentage': return 'Yüzde';
          case 'text': return 'Metin';
          default: return text;
        }
      }
    },
    {
      title: 'Otomatik Hesaplama',
      dataIndex: 'isAutoCalculated',
      key: 'isAutoCalculated',
      render: (value) => (
        <Tag color={value ? '#87d068' : '#f50'}>
          {value ? 'Evet' : 'Hayır'}
        </Tag>
      )
    },
    {
      title: 'Durum',
      dataIndex: 'isDeleted',
      key: 'isDeleted',
      render: (value) => (
        <Tag color={value ? '#f50' : '#87d068'}>
          {value ? 'Pasif' : 'Aktif'}
        </Tag>
      )
    },
    {
      title: 'İşlemler',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="primary"
            icon={<EditOutlined />}
            onClick={() => navigate(`/metrics/edit/${record.id}`)}
          >
            Düzenle
          </Button>
          <Popconfirm
            title="Bu metriği silmek istediğinizden emin misiniz?"
            onConfirm={() => handleDelete(record.id)}
            okText="Evet"
            cancelText="Hayır"
          >
            <Button
              type="primary"
              danger
              icon={<DeleteOutlined />}
            >
              Sil
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];
  
  const moreMenu = (
    <Menu>
      <Menu.Item key="sort" onClick={showSortModal} icon={<OrderedListOutlined />}>
        {selectedTemplate 
          ? `${selectedTemplate} Template'i İçin Metrik Sıralama ve Görünürlük Ayarları` 
          : `Tüm Markalar İçin Metrik Sıralama ve Görünürlük Ayarları`}
      </Menu.Item>
    </Menu>
  );

  return (
    <div style={{ padding: '24px' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>Metrik Ayarları</h2>
        <Space>
          {templates.length > 0 && (
            <Select
              placeholder="Template seçin"
              allowClear
              style={{ width: 200 }}
              value={selectedTemplate}
              onChange={(value) => setSelectedTemplate(value)}
            >
              {templates.map(template => (
                <Select.Option key={template} value={template}>{template}</Select.Option>
              ))}
            </Select>
          )}
          <Dropdown overlay={moreMenu} placement="bottomRight">
            <Button icon={<MoreOutlined />}>İşlemler</Button>
          </Dropdown>
          {hasBrands() && (
            <Button
              type="primary"
              icon={<CopyOutlined />}
              onClick={() => setCopyModalVisible(true)}
            >
              Metrik Kopyala
            </Button>
          )}
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => navigate('/metrics/new')}
          >
            Yeni Metrik Ekle
          </Button>
        </Space>
      </div>
      
      {selectedTemplate && (
        <Alert
          message={`${selectedTemplate} template'ine göre filtreleniyor`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          action={
            <Button size="small" onClick={() => setSelectedTemplate(null)}>
              Filtreyi Kaldır
            </Button>
          }
        />
      )}

      <Table
        columns={columns}
        dataSource={metrics}
        rowKey="id"
        loading={loading}
        pagination={{
          defaultPageSize: 10,
          showSizeChanger: true,
          pageSizeOptions: ['10', '20', '50', '100'],
          showTotal: (total) => `Toplam ${total} metrik`
        }}
      />

      <Modal
        title="Metrik Kopyala"
        open={copyModalVisible}
        onCancel={() => setCopyModalVisible(false)}
        onOk={handleCopyMetrics}
        confirmLoading={loading}
        okText="Kopyala"
        cancelText="İptal"
      >
        <Alert
          message="Metrik Kopyalama"
          description="Bu işlem, seçilen kaynak markadaki tüm metrikleri hedef markaya kopyalayacaktır. Hedef markada zaten var olan metrikler güncellenecektir."
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form
          form={copyForm}
          layout="vertical"
          initialValues={{
            sourceBrandId: brands.length > 0 ? brands[0].id : undefined,
            targetBrandId: brands.length > 1 ? brands[1].id : undefined
          }}
        >
          <Form.Item
            name="sourceBrandId"
            label="Kaynak Marka"
            rules={[
              { required: true, message: 'Lütfen kaynak markayı seçin' },
              {
                validator(_, value) {
                  if (value === copyForm.getFieldValue('targetBrandId')) {
                    return Promise.reject(new Error('Kaynak ve hedef marka aynı olamaz'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Select placeholder="Kaynak marka seçin">
              {brands.map(brand => (
                <Option key={brand.id} value={brand.id}>{brand.name}</Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="targetBrandId"
            label="Hedef Marka"
            rules={[
              { required: true, message: 'Lütfen hedef markayı seçin' },
              {
                validator(_, value) {
                  if (value === copyForm.getFieldValue('sourceBrandId')) {
                    return Promise.reject(new Error('Kaynak ve hedef marka aynı olamaz'));
                  }
                  return Promise.resolve();
                }
              }
            ]}
          >
            <Select placeholder="Hedef marka seçin">
              {brands.map(brand => (
                <Option key={brand.id} value={brand.id}>{brand.name}</Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
      
      {renderSortModal()}
    </div>
  );
};

export default MetricSettings; 